import { useId, useState } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Abas, PainelAba, type ItemAba } from '../../componentes/abas';
import { ConteudoAjuda } from '../../componentes/ajuda-tela';
import { Drawer } from '../../componentes/sobreposicao';
import { chaveDaTelaPeloCaminho } from '../../nucleo/chave-da-tela';
import { useSessao } from '../../nucleo/sessao';
import { opcoesDoAtalho, RegistroRapido } from './AtalhoRapido';
import { ConversaCopiloto, perguntasDoCopiloto } from './Copiloto';

/**
 * Ajustes do CRM 27/10 (K12, decisão 13): na casca nova, um botão só, "Malgo IA", com o ícone de farol (o do Copiloto do
 * clone do RD), ao lado da busca. Abre o painel com três abas: Copiloto, Ajuda desta tela e Registro rápido. Cada aba some
 * sem o que mostrar (Copiloto sem pergunta liberada, Registro sem Tarefas nem Equipe); a Ajuda sempre existe. A casca de
 * sempre não muda (Ajuda na Secao, Atalho no topo).
 */

type AbaIA = 'copiloto' | 'ajuda' | 'registro';

/** O farol do clone (símbolo `#copilot`), no traço dos ícones do app. */
export function IconeFarol({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M9 5l3-2.5L15 5zM9.5 5h5v3h-5zM10 8l-1.5 11h7L14 8M9.3 13h5.4M5 4.5l2.8 1.3M19 4.5l-2.8 1.3M5.5 8.5l2.4-.6M18.5 8.5l-2.4-.6" />
      <path d="M3 21.5c1.5-1 3-1 4.5 0s3 1 4.5 0 3-1 4.5 0 3 1 4.5 0" />
    </svg>
  );
}

/** As abas que a pessoa tem (função pura, testada em `tests/crm-comercial-ajustes.test.ts`). */
export function abasDaMalgoIA(s: { instalado(slug: string): boolean; pode(permissao: string): boolean }): AbaIA[] {
  return [...(perguntasDoCopiloto(s).length ? (['copiloto'] as const) : []), 'ajuda', ...(opcoesDoAtalho(s).length ? (['registro'] as const) : [])];
}

export function BotaoMalgoIA({ classe }: { classe: string }) {
  const { t } = useTranslation();
  const s = useSessao();
  const local = useLocation();
  const idBase = useId();
  const [aberto, setAberto] = useState(false);
  const abas = abasDaMalgoIA(s);
  const [escolhida, setEscolhida] = useState<AbaIA>(abas[0]!);
  const ativa = abas.includes(escolhida) ? escolhida : abas[0]!;
  const tela = chaveDaTelaPeloCaminho(local.pathname);
  const caminho = tela.slug && tela.rotulo ? t(`${tela.slug}:${tela.rotulo}`) : t('menus.inicio');
  const itens: ItemAba<AbaIA>[] = abas.map((a) => ({ chave: a, rotulo: t(`malgoIA.abas.${a}`) }));
  const fechar = () => setAberto(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setAberto(true)}
        className={`${classe} text-ciano`}
        title={t('malgoIA.titulo')}
        aria-label={t('malgoIA.titulo')}
        aria-haspopup="dialog"
        aria-expanded={aberto}
        data-malgo-ia=""
      >
        {/* Lote crm5 (E9): só o ícone, em qualquer largura; o nome fica no title e no aria-label. */}
        <IconeFarol className="size-5 shrink-0" />
      </button>
      <Drawer
        aberto={aberto}
        aoFechar={fechar}
        largura="lg"
        titulo={
          <span className="flex items-center gap-2">
            <IconeFarol className="size-5 text-ciano" />
            {t('malgoIA.titulo')}
          </span>
        }
      >
        <div className="flex flex-col gap-4" data-painel-malgo-ia="">
          <Abas itens={itens} ativa={ativa} aoMudar={setEscolhida} rotulo={t('malgoIA.titulo')} idBase={idBase} />
          <PainelAba idBase={idBase} chave={ativa}>
            {ativa === 'copiloto' && <ConversaCopiloto aoNavegar={fechar} />}
            {ativa === 'ajuda' && <ConteudoAjuda chave={tela.chave} caminho={caminho} aoNavegar={fechar} />}
            {ativa === 'registro' && <RegistroRapido aoConcluir={fechar} />}
          </PainelAba>
        </div>
      </Drawer>
    </>
  );
}
